'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PriceAlertModalProps {
  fishName: string;
  currentPrice: number;
  onClose: () => void;
  onSetAlert: (targetPrice: number, alertType: 'above' | 'below') => void;
}

const PriceAlertModal = ({ fishName, currentPrice, onClose, onSetAlert }: PriceAlertModalProps) => {
  const [targetPrice, setTargetPrice] = useState<string>(currentPrice.toString());
  const [alertType, setAlertType] = useState<'above' | 'below'>('below');
  const [notificationMethod, setNotificationMethod] = useState<'email' | 'sms' | 'both'>('email');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const price = parseFloat(targetPrice);
    if (!isNaN(price) && price > 0) {
      onSetAlert(price, alertType);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-md mx-4 bg-card border border-border rounded-lg shadow-elevation-xl animate-slide-down">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-md">
              <Icon name="BellIcon" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-xl text-foreground">Set Price Alert</h2>
              <p className="text-sm text-muted-foreground">{fishName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-muted transition-smooth"
            aria-label="Close modal"
          >
            <Icon name="XMarkIcon" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-foreground">Current Price</label>
              <span className="font-data text-lg font-bold text-primary">₹{currentPrice.toFixed(2)}/kg</span>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Alert Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setAlertType('below')}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-md border-2 transition-smooth ${
                  alertType === 'below' ?'border-primary bg-primary/10 text-primary' :'border-border bg-muted text-foreground hover:bg-border'
                }`}
              >
                <Icon name="ArrowTrendingDownIcon" size={20} />
                <span className="font-medium">Below</span>
              </button>
              <button
                type="button"
                onClick={() => setAlertType('above')}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-md border-2 transition-smooth ${
                  alertType === 'above' ?'border-primary bg-primary/10 text-primary' :'border-border bg-muted text-foreground hover:bg-border'
                }`}
              >
                <Icon name="ArrowTrendingUpIcon" size={20} />
                <span className="font-medium">Above</span>
              </button>
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="targetPrice" className="block text-sm font-medium text-foreground mb-2">
              Target Price (₹/kg)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
              <input
                type="number"
                id="targetPrice"
                value={targetPrice}
                onChange={(e) => setTargetPrice(e.target.value)}
                step="0.01"
                min="0"
                className="w-full pl-8 pr-4 py-3 bg-background border border-input rounded-md text-foreground focus-ring"
                placeholder="Enter target price"
                required
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Notification Method
            </label>
            <div className="space-y-2">
              {[
                { value: 'email' as const, label: 'Email', icon: 'EnvelopeIcon' },
                { value: 'sms' as const, label: 'SMS', icon: 'DevicePhoneMobileIcon' },
                { value: 'both' as const, label: 'Email & SMS', icon: 'BellAlertIcon' },
              ].map((method) => (
                <button
                  key={method.value}
                  type="button"
                  onClick={() => setNotificationMethod(method.value)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-md border transition-smooth ${
                    notificationMethod === method.value
                      ? 'border-primary bg-primary/10 text-primary' :'border-border bg-muted text-foreground hover:bg-border'
                  }`}
                >
                  <Icon name={method.icon as any} size={20} />
                  <span className="font-medium">{method.label}</span>
                  {notificationMethod === method.value && (
                    <Icon name="CheckCircleIcon" size={20} className="ml-auto" />
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-muted text-foreground rounded-md font-medium transition-smooth hover:bg-border"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-md font-medium transition-smooth hover:shadow-elevation-md hover-lift"
            >
              Set Alert
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PriceAlertModal;